import { isPerUnitProductVariant, isTiersProductVariant } from '@weft/services';
import type { CommitmentLineItem,
  CommitmentTierUsageData, LineItemProductCommitment, LineItemProductVariant,
  PriceTier } from '@weft/types';
import { MAX_TO } from '@weft/types';
import { getMapEntries,
  getMapValueOrDefault,
  getRequiredMapValue,
  getRequiredObjectProperty, isNullOrUndefined,
  mapBy, raise } from '@weft/utils';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import { formatPercentage, shortenNumber } from 'apps/app/src/utils/number';
import cn from 'classnames';
import { sumBy } from 'lodash';
import React, { useMemo } from 'react';
import Table from '../../../../components/design/table/Table';
import { useInvoiceCurrency } from '../../../../hooks/use-invoice-currency';
import { getProductTitle } from '../../../../services/product/product-title';
import { buildCommitmentLineItemTiersTableColumns } from '../commitment-line-item-tiers-table-columns-builder';
import type { CommitmentLineItemTierRow } from '../invoice-types/commitment-line-item-tier-row';
import { InvoiceSectionHeader } from './InvoiceSectionHeader';

type InvoiceTiersVolumeChargesProps = {
  lineItems: CommitmentLineItem[];  
};

type CommitmentGroup = [string, CommitmentLineItem[]];

export const InvoiceConsumptionItemsCharges:React.FC<InvoiceTiersVolumeChargesProps> = ({
  lineItems,    
}) => {
  const commitmentsById = useMemo(() => mapBy(lineItems.flatMap(lineItem => lineItem.lineItemProduct.commitment!), 'commitmentId'), [lineItems]);
  const lineItemByCommitmentId = useMemo(() => getLineItemsByCommitmentId(lineItems), [lineItems]);
  const commitmentGroups: CommitmentGroup[] = getMapEntries(lineItemByCommitmentId);
  
  const currency = useInvoiceCurrency(lineItems);
  const currencySymbol = getCurrencySymbol(currency);
  const columns = useMemo(() => buildCommitmentLineItemTiersTableColumns(currencySymbol), [currencySymbol]);
  
  return (
    <>
      {
    commitmentGroups.map(([commitmentId, commitmentGroupLineItems]) => (
      <div
        key={commitmentId}
        className="rounded-2lg border-line border"
      >
        {commitmentGroupLineItems.map((lineItem) =>
          (
            <React.Fragment key={lineItem.id}>
              <InvoiceSectionHeader
                className="p-5"
                name={getProductTitle(lineItem.lineItemProduct)}
                description={lineItem.lineItemProduct.variants[0].description}
                price={getCommitmentGroupPrice(commitmentGroupLineItems)}
                currency={currency}
              />
              <div key={lineItem.lineItemProduct.productId}>
                {lineItem.lineItemProduct.variants.map((productVariant) => {
                  return (
                    <React.Fragment key={productVariant.productVariantId}>
                      <div className="bg-light-gray px-5 py-2 font-medium text-black">
                        {productVariant.meter?.name}
                      </div>
                      <div className="p-5">
                        <Table {...{
                          rows: buildRows(lineItem, productVariant),
                          columns,
                          tableClassName: 'w-full',
                          headerClassName: 'text-placeholder border-b border-b-line',
                          cellClassName: 'py-1.5',
                        }}
                        />
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <div>
                <div className="bg-light-gray min-h-[calc(0.5rem*2+16px*1.5)] font-medium text-black" />
                <div className='flex flex-row items-center'>
                  <div className="flex flex-row items-center gap-1.5 p-5">
                    <span className='text-label'>Commitment</span>
                    <span className="text-xl font-semibold">{`${shortenNumber(getRequiredMapValue(commitmentsById, commitmentId).amount)} units`}</span>
                  </div>
                  <div className="flex flex-row items-center gap-1.5 p-5">
                    <span className='text-label'>YTD</span>
                    <span className={cn(
                      'text-xl font-semibold',
                      { 'text-grayed-out': getRelativeLineItemConsumption(lineItem.lineItemProduct.commitment) > 100 },
                    )}
                    >
                      {`${shortenNumber(lineItem.lineItemProduct.commitment?.consumption)} (${formatPercentage(getRelativeLineItemConsumption(lineItem.lineItemProduct.commitment))})`}
                    </span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ),
        )}
      </div>
    ))}
    </>
  );
};

function getLineItemsByCommitmentId(lineItems: CommitmentLineItem[]) {
  return lineItems.reduce<Map<string, CommitmentLineItem[]>>(
    (lineItemsByCommitmentId, lineItem) => {
      const commitmentId = lineItem.lineItemProduct.commitment!.commitmentId;
      const existingLineItems = getMapValueOrDefault(lineItemsByCommitmentId, commitmentId, []);

      return lineItemsByCommitmentId.set(
        commitmentId,
        [...existingLineItems, lineItem],
      );
    },
    new Map<string, CommitmentLineItem[]>,
  );
}

function getCommitmentGroupPrice(commitmentGroupLineItems: CommitmentLineItem[]) {
  return sumBy(commitmentGroupLineItems, 'price');
}

function buildRows(lineItem: CommitmentLineItem, productVariant: LineItemProductVariant) {
  const tiers = getUsageProductVariantTiers(productVariant);
  const tiersUsageData = getRequiredObjectProperty(lineItem.commitmentTiersUsageMap, productVariant.productVariantId!);

  return tiersUsageData.map(tierUsageData => buildCommitmentLineItemTierRow(tiers, tierUsageData)).sort((previous, current) => previous.tierIndex - current.tierIndex);
}

function getUsageProductVariantTiers(productVariant: LineItemProductVariant): PriceTier[] {
  if (isTiersProductVariant(productVariant)) {
    return productVariant.pricing.priceConfiguration.tiers;
  }

  if (isPerUnitProductVariant(productVariant)) {
    return [{
      price: productVariant.pricing.priceConfiguration.price,
      from: 0,
      to: MAX_TO,
    }];
  }

  return raise(`Product variant ${productVariant.productVariantId} is not a usage product variant`);
}

function buildCommitmentLineItemTierRow(tiers: PriceTier[], tierUsageData: CommitmentTierUsageData): CommitmentLineItemTierRow {
  const tier = tiers[tierUsageData.tierIndex];

  return {
    ...tier,
    rate: tier.price,
    ...tierUsageData,
  };
}

function getRelativeLineItemConsumption(lineItemProductCommitment: LineItemProductCommitment | undefined) {
  if (isNullOrUndefined(lineItemProductCommitment) || !lineItemProductCommitment.amount) {
    return 0;
  }

  return lineItemProductCommitment.consumption / lineItemProductCommitment.amount;
}
